/**
 * Route Registry
 *
 * Manages route registration, filtering, and metadata access based on platform configuration.
 */

import React from "react";

import { RouteObject } from "react-router";

import { FeatureFlags } from "../feature-flags/FeatureFlags";
import debugLogger from "../utils/debugLogger";

import { PlatformConfigManager } from "./PlatformConfig";
import { RouteMetadata, MaturityLevel } from "./types";

export class RouteRegistry {
  private platformConfig: PlatformConfigManager;
  private featureFlags: FeatureFlags;
  private routeCache: Map<string, RouteObject> = new Map();

  constructor(
    platformConfig: PlatformConfigManager,
    featureFlags: FeatureFlags,
  ) {
    this.platformConfig = platformConfig;
    this.featureFlags = featureFlags;
  }

  /**
   * Get route metadata by path
   */
  public getRoute(path: string): RouteMetadata | undefined {
    return this.platformConfig.getRoute(path);
  }

  /**
   * Get all routes for a business domain
   */
  public getRoutesByBusinessDomain(businessDomain: string): RouteMetadata[] {
    const domain = this.platformConfig.getBusinessDomain(businessDomain);
    if (!domain) return [];

    const routes: RouteMetadata[] = [];
    domain.features.forEach((feature) => {
      routes.push(...feature.routes);
    });

    return routes;
  }

  /**
   * Get all routes for a feature
   */
  public getRoutesByFeature(
    businessDomain: string,
    featureName: string,
  ): RouteMetadata[] {
    const feature = this.platformConfig.getFeature(businessDomain, featureName);
    return feature?.routes || [];
  }

  /**
   * Get enabled routes for a business domain
   */
  public getEnabledRoutes(
    businessDomain: string,
    userId?: string,
  ): RouteMetadata[] {
    return this.platformConfig.getEnabledRoutes(businessDomain, userId);
  }

  /**
   * Get enabled routes for a feature
   */
  public getEnabledRoutesByFeature(
    businessDomain: string,
    featureName: string,
    userId?: string,
  ): RouteMetadata[] {
    const feature = this.platformConfig.getFeature(businessDomain, featureName);
    if (!feature) return [];

    return feature.routes.filter((route) =>
      this.platformConfig.isRouteEnabled(route.path, userId),
    );
  }

  /**
   * Check if route is enabled
   */
  public isRouteEnabled(routePath: string, userId?: string): boolean {
    return this.platformConfig.isRouteEnabled(routePath, userId);
  }

  /**
   * Get routes filtered by maturity level
   */
  public getRoutesByMaturity(maturity: MaturityLevel): RouteMetadata[] {
    return this.platformConfig.getRoutesByMaturity(maturity);
  }

  /**
   * Get routes filtered by feature flag
   */
  public getRoutesByFeatureFlag(
    flagName: string,
    userId?: string,
  ): RouteMetadata[] {
    if (!this.featureFlags.isEnabled(flagName, userId)) {
      return [];
    }

    const allRoutes = Array.from(
      this.platformConfig.getBusinessDomains(),
    ).flatMap((domain) => domain.features.flatMap((feature) => feature.routes));

    return allRoutes.filter((route) => route.featureFlags.includes(flagName));
  }

  /**
   * Match route path with pattern (supports wildcards)
   */
  public matchRoute(path: string, pattern: string): boolean {
    if (pattern.endsWith("*")) {
      const basePattern = pattern.slice(0, -1);
      return path.startsWith(basePattern);
    }
    return path === pattern;
  }

  /**
   * Find route metadata that matches a given path
   */
  public findRouteByPath(path: string): RouteMetadata | undefined {
    const allRoutes = Array.from(
      this.platformConfig.getBusinessDomains(),
    ).flatMap((domain) => domain.features.flatMap((feature) => feature.routes));

    // First try exact match
    let route = allRoutes.find((r) => r.path === path);
    if (route) return route;

    // Then try pattern matching
    route = allRoutes.find((r) => this.matchRoute(path, r.path));
    return route;
  }

  /**
   * Get all enabled routes as RouteObject array for React Router
   * This will be used to generate routes dynamically
   */
  public async generateRouteObjects(userId?: string): Promise<RouteObject[]> {
    const routeObjects: RouteObject[] = [];
    const businessDomains = this.platformConfig.getBusinessDomains();

    for (const domain of businessDomains) {
      for (const feature of domain.features) {
        for (const route of feature.routes) {
          if (this.isRouteEnabled(route.path, userId)) {
            try {
              // Load component dynamically
              const component = await this.loadRouteComponent(route.component);

              routeObjects.push({
                path: route.path,
                element: React.createElement(component),
              });
            } catch (error) {
              debugLogger.warn(
                `Failed to load route component '${route.component}' for path '${route.path}':`,
                error,
              );
            }
          }
        }
      }
    }

    return routeObjects;
  }

  /**
   * Load route component dynamically
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async loadRouteComponent(componentName: string): Promise<any> {
    // Try to find component in platform config
    const component = this.platformConfig.getComponent(componentName);
    if (component) {
      // Use ComponentRegistry to load it
      // For now, we'll use a simple import
      const modulePath = `/src/${component.path}.tsx`;
      const module = await import(/* @vite-ignore */ modulePath);
      return module.default || module[componentName] || module[component.name];
    }

    // If not found in config, try to import directly by name
    // This is a fallback for components not yet in config
    throw new Error(
      `Component '${componentName}' not found in platform config`,
    );
  }

  /**
   * Clear route cache
   */
  public clearCache(): void {
    this.routeCache.clear();
  }

  /**
   * Get route metadata with full context
   */
  public getRouteMetadata(routePath: string): RouteMetadata | null {
    return this.getRoute(routePath) || null;
  }

  /**
   * Search routes by path pattern
   */
  public searchRoutes(pattern: string): RouteMetadata[] {
    const allRoutes = Array.from(
      this.platformConfig.getBusinessDomains(),
    ).flatMap((domain) => domain.features.flatMap((feature) => feature.routes));

    const regex = new RegExp(pattern, "i");
    return allRoutes.filter(
      (route) =>
        regex.test(route.path) ||
        (route.description && regex.test(route.description)),
    );
  }
}
